import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  User, 
  Palette, 
  Database,
  Plus,
  Trash2,
  Loader2,
  Moon,
  Sun,
  FileType,
  GitBranch,
  Edit,
  LayoutTemplate,
  Bell,
  Mail,
  Send,
  Cloud,
  CloudOff,
  HardDrive,
  Check,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Settings2,
  FileText,
  TrendingUp,
  PieChart,
  FolderOpen,
  Clock,
  BarChart3,
  Layers,
  CheckCircle,
  Tag,
  ChevronUp,
  ChevronDown,
  Inbox,
  Building2,
  Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { token, user, hasPermission } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [systemSubTab, setSystemSubTab] = useState('appearance');
  const [metadataFields, setMetadataFields] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [revisionCodes, setRevisionCodes] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [approvalCodes, setApprovalCodes] = useState([]);
  const [statusCodes, setStatusCodes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [corrFolders, setCorrFolders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [transmittalStatuses, setTransmittalStatuses] = useState([]);
  const [emailSettings, setEmailSettings] = useState({ sender_email: '', sender_name: '', has_api_key: false });
  const [loading, setLoading] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  
  // Modals
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [showDocTypeModal, setShowDocTypeModal] = useState(false);
  const [showRevCodeModal, setShowRevCodeModal] = useState(false);
  const [showDisciplineModal, setShowDisciplineModal] = useState(false);
  const [showApprovalCodeModal, setShowApprovalCodeModal] = useState(false);
  const [showStatusCodeModal, setShowStatusCodeModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showCorrFolderModal, setShowCorrFolderModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showTransmittalStatusModal, setShowTransmittalStatusModal] = useState(false);
  
  // Email settings form
  const [emailFormData, setEmailFormData] = useState({
    sender_email: '',
    sender_name: '',
    sendgrid_api_key: ''
  });
  
  // Form data
  const [fieldFormData, setFieldFormData] = useState({
    name: '',
    field_type: 'text',
    entity_type: 'document',
    options: '',
    required: false
  });
  const [docTypeFormData, setDocTypeFormData] = useState({
    name: '',
    code: '',
    description: ''
  });
  const [revCodeFormData, setRevCodeFormData] = useState({
    code: '',
    name: '',
    description: '',
    order: 0
  });
  const [disciplineFormData, setDisciplineFormData] = useState({
    code: '',
    name: '',
    description: ''
  });
  const [approvalCodeFormData, setApprovalCodeFormData] = useState({
    code: '',
    name: '',
    description: ''
  });
  const [statusCodeFormData, setStatusCodeFormData] = useState({
    code: '',
    name: '',
    description: '',
    color: ''
  });
  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    description: '',
    document_type_id: '',
    default_tags: '',
    content_template: ''
  });
  const [corrFolderFormData, setCorrFolderFormData] = useState({
    name: '',
    description: '',
    parent_id: ''
  });
  const [contactFormData, setContactFormData] = useState({
    name: '',
    contact_type: 'client',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    contact_person: '',
    notes: ''
  });
  const [transmittalStatusFormData, setTransmittalStatusFormData] = useState({
    code: '',
    name: '',
    description: '',
    color: '',
    order: 0
  });
  
  const [editingDocType, setEditingDocType] = useState(null);
  const [editingRevCode, setEditingRevCode] = useState(null);
  const [editingDiscipline, setEditingDiscipline] = useState(null);
  const [editingApprovalCode, setEditingApprovalCode] = useState(null);
  const [editingStatusCode, setEditingStatusCode] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingCorrFolder, setEditingCorrFolder] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [editingTransmittalStatus, setEditingTransmittalStatus] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileFormData, setProfileFormData] = useState({
    full_name: '',
    email: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Cloud Storage state
  const [storageProviders, setStorageProviders] = useState([]);
  const [providerTypes, setProviderTypes] = useState({});
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [storageFormData, setStorageFormData] = useState({
    name: '',
    provider_type: '',
    config: {},
    is_global_default: false
  });
  const [testingProvider, setTestingProvider] = useState(null);
  const [storageStats, setStorageStats] = useState({ total_size: '0 B', total_files: 0, active_provider: null });
  const [loadingStats, setLoadingStats] = useState(false);

  // Database Integration state
  const [databaseProviders, setDatabaseProviders] = useState([]);
  const [databaseProviderTypes, setDatabaseProviderTypes] = useState({});
  const [showDatabaseModal, setShowDatabaseModal] = useState(false);
  const [editingDbProvider, setEditingDbProvider] = useState(null);
  const [databaseFormData, setDatabaseFormData] = useState({
    name: '',
    provider_type: '',
    config_mode: 'simple',
    config: {},
    usage_mode: 'primary',
    is_active: true
  });
  const [testingDbProvider, setTestingDbProvider] = useState(null);
  const [dbStatus, setDbStatus] = useState({ connected: true, database_type: 'MongoDB' });

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Select (Dropdown)' },
    { value: 'multiselect', label: 'Multi-Select' }
  ];

  const fetchMetadataFields = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/metadata-fields`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetadataFields(response.data.fields);
    } catch (error) {
      console.error('Failed to load metadata fields');
    }
  }, [token]);

  const fetchDocumentTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/document-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocumentTypes(response.data.document_types);
    } catch (error) {
      console.error('Failed to load document types');
    }
  }, [token]);

  const fetchRevisionCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/revision-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRevisionCodes(response.data.revision_codes);
    } catch (error) {
      console.error('Failed to load revision codes');
    }
  }, [token]);

  const fetchDisciplines = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/disciplines`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDisciplines(response.data.disciplines);
    } catch (error) {
      console.error('Failed to load disciplines');
    }
  }, [token]);

  const fetchApprovalCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/approval-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApprovalCodes(response.data.approval_codes);
    } catch (error) {
      console.error('Failed to load approval codes');
    }
  }, [token]);

  const fetchStatusCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/status-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatusCodes(response.data.status_codes);
    } catch (error) {
      console.error('Failed to load status codes');
    }
  }, [token]);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/document-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates);
    } catch (error) {
      console.error('Failed to load templates');
    }
  }, [token]);

  const fetchCorrFolders = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/correspondence-folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCorrFolders(response.data.folders);
    } catch (error) {
      console.error('Failed to load correspondence folders');
    }
  }, [token]);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data.contacts);
    } catch (error) {
      console.error('Failed to load contacts');
    }
  }, [token]);

  const fetchTransmittalStatuses = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/transmittal-statuses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransmittalStatuses(response.data.transmittal_statuses);
    } catch (error) {
      console.error('Failed to load transmittal statuses');
    }
  }, [token]);

  const fetchEmailSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailSettings(response.data);
      setEmailFormData({
        sender_email: response.data.sender_email || '',
        sender_name: response.data.sender_name || '',
        sendgrid_api_key: ''
      });
    } catch (error) {
      console.error('Failed to load email settings');
    }
  }, [token]);

  // Cloud Storage fetch functions
  const fetchStorageProviders = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/storage-providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStorageProviders(response.data.storage_providers);
    } catch (error) {
      console.error('Failed to load storage providers');
    }
  }, [token]);

  const fetchProviderTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/storage-providers/types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProviderTypes(response.data.provider_types);
    } catch (error) {
      console.error('Failed to load provider types');
    }
  }, [token]);

  const fetchStorageStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const response = await axios.get(`${API_URL}/api/storage/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      
      // Transform data for the UI
      setStorageStats({
        total_size: data.summary?.total_storage_formatted || '0 B',
        total_files: data.summary?.file_count || 0,
        active_provider: data.providers?.find(p => p.is_active)?.name || null,
        file_types: (data.file_types || []).map((ft, idx, arr) => ({
          type: ft.type?.toUpperCase() || 'Unknown',
          count: ft.count,
          size: ft.size_formatted || '0 B',
          percentage: data.summary?.file_count > 0 ? Math.round((ft.count / data.summary.file_count) * 100) : 0
        })),
        top_projects: (data.storage_by_project || []).map(p => ({
          name: p.project_name || 'Unassigned',
          size: typeof p.total_size === 'number' ? formatBytes(p.total_size) : '0 B',
          files: p.file_count || 0
        })),
        recent_uploads: (data.recent_uploads || []).map(u => ({
          name: u.name,
          type: u.file_type?.toUpperCase() || 'Unknown',
          size: typeof u.file_size === 'number' ? formatBytes(u.file_size) : '0 B',
          uploaded_at: u.created_at ? new Date(u.created_at).toLocaleDateString() : 'Unknown'
        }))
      });
    } catch (error) {
      console.error('Failed to load storage stats');
    } finally {
      setLoadingStats(false);
    }
  }, [token]);

  // Helper function to format bytes
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Database Provider fetch functions
  const fetchDatabaseProviders = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/database-providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDatabaseProviders(response.data.database_providers);
    } catch (error) {
      console.error('Failed to load database providers');
    }
  }, [token]);

  const fetchDatabaseProviderTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/database-providers/types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDatabaseProviderTypes(response.data.provider_types);
    } catch (error) {
      console.error('Failed to load database provider types');
    }
  }, [token]);

  const fetchDbStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/database-providers/status/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      
      // Transform response to expected format
      setDbStatus({
        connected: data.local_mongodb?.status === 'connected',
        database_type: data.current_mode === 'external' ? data.primary_provider?.provider_type : 'MongoDB',
        database_name: data.local_mongodb?.database_name || 'Unknown',
        collections: data.local_mongodb?.collections || [],
        current_mode: data.current_mode,
        primary_provider: data.primary_provider,
        sync_providers_count: data.sync_providers_count
      });
    } catch (error) {
      console.error('Failed to load database status');
      // Keep default connected state if API fails but app is working
      setDbStatus({ connected: true, database_type: 'MongoDB' });
    }
  }, [token]);

  useEffect(() => {
    fetchMetadataFields();
    fetchDocumentTypes();
    fetchRevisionCodes();
    fetchDisciplines();
    fetchApprovalCodes();
    fetchStatusCodes();
    fetchTemplates();
    if (hasPermission('admin')) {
      fetchStorageProviders();
      fetchProviderTypes();
      fetchStorageStats();
      fetchDatabaseProviders();
      fetchDatabaseProviderTypes();
      fetchDbStatus();
      fetchCorrFolders();
      fetchContacts();
      fetchTransmittalStatuses();
      fetchEmailSettings();
    }
  }, [fetchMetadataFields, fetchDocumentTypes, fetchRevisionCodes, fetchDisciplines, fetchApprovalCodes, fetchStatusCodes, fetchTemplates, fetchStorageProviders, fetchProviderTypes, fetchStorageStats, fetchDatabaseProviders, fetchDatabaseProviderTypes, fetchDbStatus, fetchCorrFolders, fetchContacts, fetchTransmittalStatuses, fetchEmailSettings, hasPermission]);

  // Metadata Field handlers
  const handleCreateField = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/metadata-fields`, {
        ...fieldFormData,
        options: fieldFormData.options.split(',').map(o => o.trim()).filter(Boolean)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Metadata field created');
      setShowFieldModal(false);
      setFieldFormData({ name: '', field_type: 'text', entity_type: 'document', options: '', required: false });
      fetchMetadataFields();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create field');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteField = async (fieldId) => {
    if (!window.confirm('Delete this metadata field?')) return;
    try {
      await axios.delete(`${API_URL}/api/metadata-fields/${fieldId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Field deleted');
      fetchMetadataFields();
    } catch (error) {
      toast.error('Failed to delete field');
    }
  };

  // Document Type handlers
  const handleSaveDocType = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingDocType) {
        await axios.put(`${API_URL}/api/document-types/${editingDocType.id}`, docTypeFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Document type updated');
      } else {
        await axios.post(`${API_URL}/api/document-types`, docTypeFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Document type created');
      }
      setShowDocTypeModal(false);
      setDocTypeFormData({ name: '', code: '', description: '' });
      setEditingDocType(null);
      fetchDocumentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save document type');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocType = async (typeId) => {
    if (!window.confirm('Delete this document type?')) return;
    try {
      await axios.delete(`${API_URL}/api/document-types/${typeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document type deleted');
      fetchDocumentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document type');
    }
  };

  const openEditDocType = (dt) => {
    setEditingDocType(dt);
    setDocTypeFormData({ name: dt.name, code: dt.code, description: dt.description || '' });
    setShowDocTypeModal(true);
  };

  // Revision Code handlers
  const handleSaveRevCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingRevCode) {
        await axios.put(`${API_URL}/api/revision-codes/${editingRevCode.id}`, revCodeFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Revision code updated');
      } else {
        await axios.post(`${API_URL}/api/revision-codes`, revCodeFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Revision code created');
      }
      setShowRevCodeModal(false);
      setRevCodeFormData({ code: '', name: '', description: '', order: 0 });
      setEditingRevCode(null);
      fetchRevisionCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save revision code');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRevCode = async (codeId) => {
    if (!window.confirm('Delete this revision code?')) return;
    try {
      await axios.delete(`${API_URL}/api/revision-codes/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Revision code deleted');
      fetchRevisionCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete revision code');
    }
  };

  const openEditRevCode = (rc) => {
    setEditingRevCode(rc);
    setRevCodeFormData({ code: rc.code, name: rc.name, description: rc.description || '', order: rc.order || 0 });
    setShowRevCodeModal(true);
  };

  // Discipline handlers
  const handleSaveDiscipline = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingDiscipline) {
        await axios.put(`${API_URL}/api/disciplines/${editingDiscipline.id}`, disciplineFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Discipline updated');
      } else {
        await axios.post(`${API_URL}/api/disciplines`, disciplineFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Discipline created');
      }
      setShowDisciplineModal(false);
      setDisciplineFormData({ code: '', name: '', description: '' });
      setEditingDiscipline(null);
      fetchDisciplines();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save discipline');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDiscipline = async (disciplineId) => {
    if (!window.confirm('Delete this discipline?')) return;
    try {
      await axios.delete(`${API_URL}/api/disciplines/${disciplineId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Discipline deleted');
      fetchDisciplines();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete discipline');
    }
  };

  const openEditDiscipline = (d) => {
    setEditingDiscipline(d);
    setDisciplineFormData({ code: d.code, name: d.name, description: d.description || '' });
    setShowDisciplineModal(true);
  };

  // Approval Code handlers
  const handleSaveApprovalCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingApprovalCode) {
        await axios.put(`${API_URL}/api/approval-codes/${editingApprovalCode.id}`, approvalCodeFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Approval code updated');
      } else {
        await axios.post(`${API_URL}/api/approval-codes`, approvalCodeFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Approval code created');
      }
      setShowApprovalCodeModal(false);
      setApprovalCodeFormData({ code: '', name: '', description: '' });
      setEditingApprovalCode(null);
      fetchApprovalCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save approval code');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteApprovalCode = async (codeId) => {
    if (!window.confirm('Delete this approval code?')) return;
    try {
      await axios.delete(`${API_URL}/api/approval-codes/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Approval code deleted');
      fetchApprovalCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete approval code');
    }
  };

  const openEditApprovalCode = (ac) => {
    setEditingApprovalCode(ac);
    setApprovalCodeFormData({ code: ac.code, name: ac.name, description: ac.description || '' });
    setShowApprovalCodeModal(true);
  };

  // Status Code handlers
  const handleSaveStatusCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingStatusCode) {
        await axios.put(`${API_URL}/api/status-codes/${editingStatusCode.id}`, statusCodeFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Status code updated');
      } else {
        await axios.post(`${API_URL}/api/status-codes`, statusCodeFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Status code created');
      }
      setShowStatusCodeModal(false);
      setStatusCodeFormData({ code: '', name: '', description: '', color: '' });
      setEditingStatusCode(null);
      fetchStatusCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save status code');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStatusCode = async (codeId) => {
    if (!window.confirm('Delete this status code?')) return;
    try {
      await axios.delete(`${API_URL}/api/status-codes/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status code deleted');
      fetchStatusCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete status code');
    }
  };

  const openEditStatusCode = (sc) => {
    setEditingStatusCode(sc);
    setStatusCodeFormData({ code: sc.code, name: sc.name, description: sc.description || '', color: sc.color || '' });
    setShowStatusCodeModal(true);
  };

  // Correspondence Folder handlers
  const handleSaveCorrFolder = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingCorrFolder) {
        await axios.put(`${API_URL}/api/correspondence-folders/${editingCorrFolder.id}`, corrFolderFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Folder updated');
      } else {
        await axios.post(`${API_URL}/api/correspondence-folders`, corrFolderFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Folder created');
      }
      setShowCorrFolderModal(false);
      setCorrFolderFormData({ name: '', description: '', parent_id: '' });
      setEditingCorrFolder(null);
      fetchCorrFolders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save folder');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCorrFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? This will fail if the folder has correspondence or subfolders.')) return;
    try {
      await axios.delete(`${API_URL}/api/correspondence-folders/${folderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Folder deleted');
      fetchCorrFolders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete folder');
    }
  };

  const openEditCorrFolder = (folder) => {
    setEditingCorrFolder(folder);
    setCorrFolderFormData({ name: folder.name, description: folder.description || '', parent_id: folder.parent_id || '' });
    setShowCorrFolderModal(true);
  };

  const handleMoveCorrFolder = async (folderId, direction, parentId = null) => {
    // Get siblings (folders with same parent)
    const siblings = corrFolders
      .filter(f => (f.parent_id || null) === parentId)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    
    const currentIndex = siblings.findIndex(f => f.id === folderId);
    
    if (currentIndex === -1) return;
    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === siblings.length - 1) return;
    
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    // Swap orders
    const newOrders = siblings.map((f, i) => {
      if (i === currentIndex) return { id: f.id, order: targetIndex };
      if (i === targetIndex) return { id: f.id, order: currentIndex };
      return { id: f.id, order: i };
    });
    
    try {
      await axios.post(`${API_URL}/api/correspondence-folders/reorder`, newOrders, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCorrFolders();
    } catch (error) {
      toast.error('Failed to reorder folders');
    }
  };

  // Contact handlers
  const handleSaveContact = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingContact) {
        await axios.put(`${API_URL}/api/contacts/${editingContact.id}`, contactFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Contact updated');
      } else {
        await axios.post(`${API_URL}/api/contacts`, contactFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Contact created');
      }
      setShowContactModal(false);
      setEditingContact(null);
      setContactFormData({ name: '', contact_type: 'client', email: '', phone: '', address: '', city: '', country: '', contact_person: '', notes: '' });
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save contact');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await axios.delete(`${API_URL}/api/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact deleted');
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete contact');
    }
  };

  const openEditContact = (contact) => {
    setEditingContact(contact);
    setContactFormData({
      name: contact.name || '',
      contact_type: contact.contact_type || 'client',
      email: contact.email || '',
      phone: contact.phone || '',
      address: contact.address || '',
      city: contact.city || '',
      country: contact.country || '',
      contact_person: contact.contact_person || '',
      notes: contact.notes || ''
    });
    setShowContactModal(true);
  };

  // Transmittal Status handlers
  const handleSaveTransmittalStatus = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingTransmittalStatus) {
        await axios.put(`${API_URL}/api/transmittal-statuses/${editingTransmittalStatus.id}`, transmittalStatusFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Transmittal status updated');
      } else {
        await axios.post(`${API_URL}/api/transmittal-statuses`, transmittalStatusFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Transmittal status created');
      }
      setShowTransmittalStatusModal(false);
      setEditingTransmittalStatus(null);
      setTransmittalStatusFormData({ code: '', name: '', description: '', color: '', order: 0 });
      fetchTransmittalStatuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save transmittal status');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransmittalStatus = async (statusId) => {
    if (!window.confirm('Delete this transmittal status?')) return;
    try {
      await axios.delete(`${API_URL}/api/transmittal-statuses/${statusId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Transmittal status deleted');
      fetchTransmittalStatuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete transmittal status');
    }
  };

  const openEditTransmittalStatus = (status) => {
    setEditingTransmittalStatus(status);
    setTransmittalStatusFormData({
      code: status.code || '',
      name: status.name || '',
      description: status.description || '',
      color: status.color || '',
      order: status.order || 0
    });
    setShowTransmittalStatusModal(true);
  };

  // Email settings handlers
  const handleSaveEmailSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {};
      if (emailFormData.sender_email) payload.sender_email = emailFormData.sender_email;
      if (emailFormData.sender_name) payload.sender_name = emailFormData.sender_name;
      if (emailFormData.sendgrid_api_key) payload.sendgrid_api_key = emailFormData.sendgrid_api_key;
      
      await axios.put(`${API_URL}/api/settings/email`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email settings updated');
      fetchEmailSettings();
      setEmailFormData(prev => ({ ...prev, sendgrid_api_key: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      const response = await axios.post(`${API_URL}/api/settings/email/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Template handlers
  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...templateFormData,
        default_tags: templateFormData.default_tags.split(',').map(t => t.trim()).filter(Boolean)
      };
      
      if (editingTemplate) {
        await axios.put(`${API_URL}/api/document-templates/${editingTemplate.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template updated');
      } else {
        await axios.post(`${API_URL}/api/document-templates`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template created');
      }
      setShowTemplateModal(false);
      setTemplateFormData({ name: '', description: '', document_type_id: '', default_tags: '', content_template: '' });
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API_URL}/api/document-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateFormData({
      name: template.name,
      description: template.description || '',
      document_type_id: template.document_type_id || '',
      default_tags: template.default_tags?.join(', ') || '',
      content_template: template.content_template || ''
    });
    setShowTemplateModal(true);
  };

  const handleTestEmail = async () => {
    setSendingTestEmail(true);
    try {
      const response = await axios.post(`${API_URL}/api/notifications/test-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Test email sent to ${response.data.recipient}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleEditProfile = () => {
    setProfileFormData({
      full_name: user?.full_name || '',
      email: user?.email || ''
    });
    setEditingProfile(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await axios.put(`${API_URL}/api/profile`, {
        full_name: profileFormData.full_name,
        email: profileFormData.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully');
      setEditingProfile(false);
      // Refresh user data - this will update the context
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  // Cloud Storage Handlers
  const handleSaveStorageProvider = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingProvider) {
        await axios.put(`${API_URL}/api/storage-providers/${editingProvider.id}`, storageFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Storage provider updated');
      } else {
        await axios.post(`${API_URL}/api/storage-providers`, storageFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Storage provider created');
      }
      setShowStorageModal(false);
      resetStorageForm();
      fetchStorageProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save storage provider');
    } finally {
      setLoading(false);
    }
  };

  const handleSetActiveStorageProvider = async (providerId) => {
    try {
      await axios.put(`${API_URL}/api/storage-providers/${providerId}`, { is_active: true }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Storage provider activated');
      fetchStorageProviders();
      fetchStorageStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate provider');
    }
  };

  const handleDeleteStorageProvider = async (providerId) => {
    if (!window.confirm('Delete this storage provider? Projects using it will need to be updated.')) return;
    try {
      await axios.delete(`${API_URL}/api/storage-providers/${providerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Storage provider deleted');
      fetchStorageProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete storage provider');
    }
  };

  const handleTestStorageProvider = async (providerId) => {
    setTestingProvider(providerId);
    try {
      const response = await axios.post(`${API_URL}/api/storage-providers/${providerId}/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.info(response.data.message);
      }
      fetchStorageProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
      fetchStorageProviders();
    } finally {
      setTestingProvider(null);
    }
  };

  const openEditStorageProvider = (provider) => {
    setEditingProvider(provider);
    setStorageFormData({
      name: provider.name,
      provider_type: provider.provider_type,
      config: { ...provider.config },
      is_global_default: provider.is_global_default
    });
    setShowStorageModal(true);
  };

  const openNewStorageProvider = () => {
    setEditingProvider(null);
    resetStorageForm();
    setShowStorageModal(true);
  };

  const resetStorageForm = () => {
    setStorageFormData({
      name: '',
      provider_type: '',
      config: {},
      is_global_default: false
    });
    setEditingProvider(null);
  };

  const handleProviderTypeChange = (type) => {
    const typeConfig = providerTypes[type];
    const defaultConfig = {};
    if (typeConfig) {
      typeConfig.config_fields.forEach(field => {
        if (field.default) {
          defaultConfig[field.key] = field.default;
        }
      });
    }
    setStorageFormData({
      ...storageFormData,
      provider_type: type,
      config: defaultConfig
    });
  };

  const getProviderStatusBadge = (status) => {
    switch (status) {
      case 'connected':
        return <Badge className="bg-green-500/10 text-green-600 border-green-200"><Check className="w-3 h-3 mr-1" /> Connected</Badge>;
      case 'error':
        return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" /> Error</Badge>;
      case 'pending_implementation':
        return <Badge variant="outline" className="text-blue-600 border-blue-200"><Cloud className="w-3 h-3 mr-1" /> Pending</Badge>;
      default:
        return <Badge variant="outline"><AlertCircle className="w-3 h-3 mr-1" /> Not Verified</Badge>;
    }
  };

  const getProviderIcon = (type) => {
    switch (type) {
      case 's3': return '☁️';
      case 'onedrive': return '📁';
      case 'google_drive': return '📂';
      case 'dropbox': return '📦';
      case 'sharepoint': return '🏢';
      default: return '💾';
    }
  };

  // Database Provider Handlers
  const handleSaveDatabaseProvider = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingDbProvider) {
        await axios.put(`${API_URL}/api/database-providers/${editingDbProvider.id}`, databaseFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Database provider updated');
      } else {
        await axios.post(`${API_URL}/api/database-providers`, databaseFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Database provider created');
      }
      setShowDatabaseModal(false);
      resetDatabaseForm();
      fetchDatabaseProviders();
      fetchDbStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save database provider');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDatabaseProvider = async (providerId) => {
    if (!window.confirm('Delete this database provider configuration?')) return;
    try {
      await axios.delete(`${API_URL}/api/database-providers/${providerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Database provider deleted');
      fetchDatabaseProviders();
      fetchDbStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete database provider');
    }
  };

  const handleTestDatabaseProvider = async (providerId) => {
    setTestingDbProvider(providerId);
    try {
      const response = await axios.post(`${API_URL}/api/database-providers/${providerId}/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.info(response.data.message);
      }
      fetchDatabaseProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
      fetchDatabaseProviders();
    } finally {
      setTestingDbProvider(null);
    }
  };

  const openEditDatabaseProvider = (provider) => {
    setEditingDbProvider(provider);
    setDatabaseFormData({
      name: provider.name,
      provider_type: provider.provider_type,
      config_mode: provider.config_mode || 'simple',
      config: { ...provider.config },
      usage_mode: provider.usage_mode,
      is_active: provider.is_active
    });
    setShowDatabaseModal(true);
  };

  const openNewDatabaseProvider = () => {
    setEditingDbProvider(null);
    resetDatabaseForm();
    setShowDatabaseModal(true);
  };

  const resetDatabaseForm = () => {
    setDatabaseFormData({
      name: '',
      provider_type: '',
      config_mode: 'simple',
      config: {},
      usage_mode: 'primary',
      is_active: true
    });
    setEditingDbProvider(null);
  };

  const handleDbProviderTypeChange = (type) => {
    const typeConfig = databaseProviderTypes[type];
    const defaultConfig = {};
    if (typeConfig) {
      const fields = databaseFormData.config_mode === 'simple' 
        ? typeConfig.simple_config 
        : typeConfig.advanced_config;
      fields?.forEach(field => {
        if (field.default !== undefined) {
          defaultConfig[field.key] = field.default;
        }
      });
    }
    setDatabaseFormData({
      ...databaseFormData,
      provider_type: type,
      config: defaultConfig
    });
  };

  const getDbProviderStatusBadge = (status) => {
    switch (status) {
      case 'connected':
        return <Badge className="bg-green-500/10 text-green-600 border-green-200"><Check className="w-3 h-3 mr-1" /> Connected</Badge>;
      case 'error':
        return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" /> Error</Badge>;
      case 'pending_implementation':
        return <Badge variant="outline" className="text-blue-600 border-blue-200"><Database className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'coming_soon':
        return <Badge variant="outline" className="text-purple-600 border-purple-200"><Clock className="w-3 h-3 mr-1" /> Coming Soon</Badge>;
      default:
        return <Badge variant="outline"><AlertCircle className="w-3 h-3 mr-1" /> Not Verified</Badge>;
    }
  };

  const getDbProviderIcon = (type) => {
    switch (type) {
      case 'postgresql': return '🐘';
      case 'mysql': return '🐬';
      case 'azure_sql': return '🔷';
      case 'google_cloud_sql': return '🌐';
      case 'mongodb_atlas': return '🍃';
      case 'aws_documentdb': return '📄';
      case 'azure_cosmos': return '🌌';
      default: return '🗄️';
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your preferences and system configuration
        </p>
      </div>

      <div className="p-8">
        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList>
            <TabsTrigger value="profile">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="system">
              <Settings2 className="w-4 h-4 mr-2" />
              System
            </TabsTrigger>
            <TabsTrigger value="notifications">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            {hasPermission('admin') && (
              <>
                <TabsTrigger value="document-types">
                  <FileType className="w-4 h-4 mr-2" />
                  Document Types
                </TabsTrigger>
                <TabsTrigger value="revision-codes">
                  <GitBranch className="w-4 h-4 mr-2" />
                  Revision Codes
                </TabsTrigger>
                <TabsTrigger value="disciplines">
                  <Layers className="w-4 h-4 mr-2" />
                  Disciplines
                </TabsTrigger>
                <TabsTrigger value="approval-codes">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approval Codes
                </TabsTrigger>
                <TabsTrigger value="status-codes">
                  <Tag className="w-4 h-4 mr-2" />
                  Status Codes
                </TabsTrigger>
                <TabsTrigger value="templates">
                  <LayoutTemplate className="w-4 h-4 mr-2" />
                  Templates
                </TabsTrigger>
                <TabsTrigger value="metadata">
                  <Database className="w-4 h-4 mr-2" />
                  Custom Fields
                </TabsTrigger>
                <TabsTrigger value="corr-folders">
                  <Inbox className="w-4 h-4 mr-2" />
                  Mail Folders
                </TabsTrigger>
                <TabsTrigger value="transmittal-statuses">
                  <Send className="w-4 h-4 mr-2" />
                  Transmittal Statuses
                </TabsTrigger>
              </>
            )}
          </TabsList>

          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Your account details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {!editingProfile ? (
                  <>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-2xl font-semibold">
                          {user?.full_name?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{user?.full_name}</h3>
                        <p className="text-sm text-muted-foreground">{user?.email}</p>
                        <p className="text-sm text-muted-foreground">Role: {user?.role_name}</p>
                      </div>
                    </div>
                    <Button variant="outline" onClick={handleEditProfile} data-testid="edit-profile-btn">
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Profile
                    </Button>
                  </>
                ) : (
                  <form onSubmit={handleSaveProfile} className="space-y-4">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-2xl font-semibold">
                          {profileFormData.full_name?.charAt(0) || 'U'}
                        </span>
                      </div>
                      <div className="flex-1 space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="profile-name">Full Name</Label>
                          <Input
                            id="profile-name"
                            value={profileFormData.full_name}
                            onChange={(e) => setProfileFormData({ ...profileFormData, full_name: e.target.value })}
                            placeholder="Enter your full name"
                            data-testid="profile-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="profile-email">Email Address</Label>
                          <Input
                            id="profile-email"
                            type="email"
                            value={profileFormData.email}
                            onChange={(e) => setProfileFormData({ ...profileFormData, email: e.target.value })}
                            placeholder="Enter your email"
                            data-testid="profile-email-input"
                          />
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">Role: {user?.role_name} (cannot be changed here)</p>
                    <div className="flex gap-2">
                      <Button type="submit" disabled={savingProfile} data-testid="save-profile-btn">
                        {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Save Changes
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setEditingProfile(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Email Notifications</CardTitle>
                <CardDescription>Configure how you receive notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg border">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">Approval Notifications</h4>
                        <p className="text-sm text-muted-foreground">
                          Get notified when documents are submitted for approval or reviewed
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-green-600 border-green-200">Enabled</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 rounded-lg border">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                        <Bell className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">Document Updates</h4>
                        <p className="text-sm text-muted-foreground">
                          Get notified when documents you created are modified or commented on
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-green-600 border-green-200">Enabled</Badge>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h4 className="font-medium mb-2">Test Email Configuration</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Send a test email to verify your notification settings are working correctly.
                  </p>
                  <Button 
                    onClick={handleTestEmail} 
                    disabled={sendingTestEmail || !user?.email}
                    data-testid="test-email-btn"
                  >
                    {sendingTestEmail ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    {sendingTestEmail ? 'Sending...' : 'Send Test Email'}
                  </Button>
                  {user?.email && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Will be sent to: {user.email}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="system">
            <Card>
              <CardHeader>
                <CardTitle>System Settings</CardTitle>
                <CardDescription>Configure appearance, storage, database, and email settings</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Sub-tabs for System */}
                <Tabs value={systemSubTab} onValueChange={setSystemSubTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-4 mb-6">
                    <TabsTrigger value="appearance">
                      <Palette className="w-4 h-4 mr-2" />
                      Appearance
                    </TabsTrigger>
                    <TabsTrigger value="cloud-storage">
                      <Cloud className="w-4 h-4 mr-2" />
                      Cloud Storage
                    </TabsTrigger>
                    <TabsTrigger value="database">
                      <HardDrive className="w-4 h-4 mr-2" />
                      Database
                    </TabsTrigger>
                    <TabsTrigger value="email">
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </TabsTrigger>
                  </TabsList>

                  {/* Appearance Sub-tab */}
                  <TabsContent value="appearance" className="mt-0">
                    <div className="space-y-6">
                      <div className="flex items-center justify-between p-4 rounded-lg border">
                        <div className="space-y-1">
                          <Label className="text-base">Theme</Label>
                          <p className="text-sm text-muted-foreground">
                            Switch between light and dark mode
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Sun className="w-4 h-4 text-muted-foreground" />
                            <Switch
                              checked={theme === 'dark'}
                              onCheckedChange={toggleTheme}
                              data-testid="theme-switch"
                            />
                            <Moon className="w-4 h-4 text-muted-foreground" />
                          </div>
                          <span className="text-sm text-muted-foreground capitalize">
                            {theme} mode
                          </span>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Cloud Storage Sub-tab */}
                  <TabsContent value="cloud-storage" className="mt-0">
                    <div className="space-y-6">
                      {/* Header Section */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold flex items-center gap-2">
                            <Cloud className="w-5 h-5" />
                            Cloud Storage Providers
                          </h3>
                          <p className="text-sm text-muted-foreground">Configure cloud storage services for document storage. Supports multiple providers with global or per-project settings.</p>
                        </div>
                        <Button onClick={() => setShowAddProviderModal(true)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Add Provider
                        </Button>
                      </div>

                      {/* Provider Overview Cards */}
                      <div className="grid grid-cols-3 gap-4">
                        <Card>
                          <CardContent className="pt-6">
                            <div className="flex items-center gap-2 mb-2">
                              <HardDrive className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">Total Providers</span>
                            </div>
                            <p className="text-3xl font-bold">{storageProviders.length}</p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="pt-6">
                            <div className="flex items-center gap-2 mb-2">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span className="text-sm text-muted-foreground">Active</span>
                            </div>
                            <p className="text-3xl font-bold">{storageProviders.filter(p => p.is_active).length}</p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="pt-6">
                            <div className="flex items-center gap-2 mb-2">
                              <ExternalLink className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">Global Default</span>
                            </div>
                            <p className="text-lg font-bold truncate">
                              {storageProviders.find(p => p.is_active)?.name || 'Local Storage'}
                            </p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Storage Usage Dashboard */}
                      <Card>
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2">
                              <BarChart3 className="w-4 h-4" />
                              Storage Usage Dashboard
                            </CardTitle>
                            <Button variant="outline" size="sm" onClick={fetchStorageStats}>
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Refresh
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-4 gap-4 mb-6">
                            <div className="p-4 rounded-lg bg-muted/30">
                              <div className="flex items-center gap-2 mb-2">
                                <FileText className="w-4 h-4 text-blue-500" />
                                <span className="text-sm text-muted-foreground">Total Documents</span>
                              </div>
                              <p className="text-2xl font-bold">{storageStats.total_documents || 0}</p>
                              <p className="text-xs text-muted-foreground">{storageStats.documents_with_files || 0} with files</p>
                            </div>
                            <div className="p-4 rounded-lg bg-muted/30">
                              <div className="flex items-center gap-2 mb-2">
                                <HardDrive className="w-4 h-4 text-green-500" />
                                <span className="text-sm text-muted-foreground">Total Storage</span>
                              </div>
                              <p className="text-2xl font-bold">{storageStats.total_size || '0 B'}</p>
                              <p className="text-xs text-muted-foreground">{storageStats.total_files || 0} files</p>
                            </div>
                            <div className="p-4 rounded-lg bg-muted/30">
                              <div className="flex items-center gap-2 mb-2">
                                <TrendingUp className="w-4 h-4 text-purple-500" />
                                <span className="text-sm text-muted-foreground">Avg File Size</span>
                              </div>
                              <p className="text-2xl font-bold">{storageStats.avg_file_size || '0 B'}</p>
                              <p className="text-xs text-muted-foreground">Max: {storageStats.max_file_size || '0 B'}</p>
                            </div>
                            <div className="p-4 rounded-lg bg-muted/30">
                              <div className="flex items-center gap-2 mb-2">
                                <FolderOpen className="w-4 h-4 text-orange-500" />
                                <span className="text-sm text-muted-foreground">Local Storage</span>
                              </div>
                              <p className="text-2xl font-bold">{storageStats.local_storage_size || '0 B'}</p>
                              <p className="text-xs text-muted-foreground">{storageStats.local_files_count || 0} files on disk</p>
                            </div>
                          </div>

                          {/* File Type Distribution & Top Projects */}
                          <div className="grid grid-cols-2 gap-6">
                            {/* File Type Distribution */}
                            <Card>
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm flex items-center gap-2">
                                  <PieChart className="w-4 h-4" />
                                  File Type Distribution
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-3">
                                  {(storageStats.file_types || []).length === 0 ? (
                                    <p className="text-sm text-muted-foreground py-4 text-center">No file type data available</p>
                                  ) : (
                                    (storageStats.file_types || []).map((item, idx) => (
                                      <div key={idx} className="flex items-center gap-3">
                                        <span className="text-sm w-24 truncate">{item.type}</span>
                                        <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                          <div 
                                            className="h-full bg-primary rounded-full transition-all"
                                            style={{ width: `${item.percentage || 0}%` }}
                                          />
                                        </div>
                                        <span className="text-xs text-muted-foreground w-16 text-right">{item.count} files</span>
                                        <span className="text-xs text-muted-foreground w-16 text-right">{item.size}</span>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </CardContent>
                            </Card>

                            {/* Top Projects by Storage */}
                            <Card>
                              <CardHeader className="pb-2">
                                <CardTitle className="text-sm flex items-center gap-2">
                                  <FolderOpen className="w-4 h-4" />
                                  Top Projects by Storage
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-3">
                                  {(storageStats.top_projects || []).length === 0 ? (
                                    <p className="text-sm text-muted-foreground py-4 text-center">No project storage data available</p>
                                  ) : (
                                    (storageStats.top_projects || []).map((project, idx) => (
                                      <div key={idx} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/30">
                                        <div className="flex items-center gap-2">
                                          <div className={`w-2 h-2 rounded-full ${idx === 0 ? 'bg-green-500' : idx === 1 ? 'bg-blue-500' : 'bg-purple-500'}`} />
                                          <span className="text-sm truncate max-w-[150px]">{project.name}</span>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-sm font-medium">{project.size}</p>
                                          <p className="text-xs text-muted-foreground">{project.files || 0} files</p>
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Recent Uploads */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Recent Uploads
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {(storageStats.recent_uploads || []).length === 0 ? (
                            <p className="text-sm text-muted-foreground py-6 text-center">No recent uploads</p>
                          ) : (
                            <div className="space-y-2">
                              {(storageStats.recent_uploads || []).map((file, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/30">
                                  <div className="flex items-center gap-3">
                                    <FileText className="w-4 h-4 text-muted-foreground" />
                                    <div>
                                      <p className="text-sm font-medium">{file.name}</p>
                                      <p className="text-xs text-muted-foreground">{file.type}</p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-sm">{file.size}</p>
                                    <p className="text-xs text-muted-foreground">{file.uploaded_at}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Supported Providers */}
                      <div>
                        <h4 className="text-sm font-medium mb-3">Supported Providers</h4>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(providerTypes).map(([key, type]) => (
                            <div key={key} className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-card">
                              <Cloud className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm font-medium">{type.name}</span>
                              <Badge variant="secondary" className="text-xs">{type.auth_type}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Configured Providers List */}
                      {storageProviders.length > 0 && (
                        <Card>
                          <CardContent className="pt-6">
                            <div className="space-y-3">
                              {storageProviders.map((provider) => (
                                <div
                                  key={provider.id}
                                  className={`p-4 rounded-lg border ${provider.is_active ? 'border-green-500 bg-green-500/5' : 'bg-card'}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <Switch 
                                        checked={provider.is_active} 
                                        onCheckedChange={() => !provider.is_active && handleSetActiveStorageProvider(provider.id)}
                                      />
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium">{provider.name}</span>
                                          {provider.is_active && (
                                            <Badge variant="secondary" className="text-xs">Global Default</Badge>
                                          )}
                                        </div>
                                        <p className="text-sm text-muted-foreground">{provider.provider_type}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline" className="gap-1">
                                        <Clock className="w-3 h-3" />
                                        {provider.status === 'verified' ? 'Verified' : 'Not Verified'}
                                      </Badge>
                                      <Button size="sm" variant="outline" onClick={() => handleTestStorageProvider(provider.id)}>
                                        <RefreshCw className="w-3 h-3 mr-1" />
                                        Test
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => openEditStorageProvider(provider)}>
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDeleteStorageProvider(provider.id)}
                                        className="text-destructive"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-2 ml-12">
                                    Configuration saved. Test connection to verify.
                                  </p>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* No Providers Configured State */}
                      {storageProviders.length === 0 && (
                        <Card>
                          <CardContent className="py-12">
                            <div className="text-center">
                              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                                <Cloud className="w-8 h-8 text-muted-foreground" />
                              </div>
                              <h4 className="font-medium mb-1">No Cloud Storage Configured</h4>
                              <p className="text-sm text-muted-foreground mb-4">Currently using local storage. Add a cloud provider to enable remote file storage.</p>
                              <Button onClick={() => setShowAddProviderModal(true)}>
                                <Plus className="w-4 h-4 mr-2" />
                                Add Your First Provider
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Provider Implementation Status */}
                      <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
                          <div>
                            <p className="font-medium text-blue-600">Provider Implementation Status</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              Cloud storage providers are currently in configuration-only mode. Save your credentials now, and the system will automatically use them when each provider is fully implemented.
                            </p>
                            <p className="text-sm text-muted-foreground mt-2">
                              <span className="font-medium">Roadmap:</span>{' '}
                              <span className="text-blue-600">AWS S3 → Dropbox → Google Drive → OneDrive → SharePoint</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Database Sub-tab */}
                  <TabsContent value="database" className="mt-0">
                    <div className="space-y-6">
                      {/* External Database Integration Header */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold">External Database Integration</h3>
                          <p className="text-sm text-muted-foreground">Configure external database services to store all application metadata. Choose your preferred cloud database provider.</p>
                        </div>
                        <Button size="sm" onClick={() => setShowAddDbProviderModal(true)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Add Provider
                        </Button>
                      </div>

                      {/* Current Database Configuration */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <ExternalLink className="w-4 h-4" />
                            Current Database Configuration
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-xs text-muted-foreground mb-1">Mode</p>
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${dbStatus.connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                <span className="font-medium">{dbStatus.current_mode === 'external' ? 'External Provider' : 'Local MongoDB'}</span>
                              </div>
                            </div>
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-xs text-muted-foreground mb-1">Database Name</p>
                              <span className="font-medium">{dbStatus.database_name || 'Unknown'}</span>
                            </div>
                            <div className="p-3 rounded-lg bg-muted/50">
                              <p className="text-xs text-muted-foreground mb-1">Collections</p>
                              <span className="font-medium">{dbStatus.collections?.length || 0} tables</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Supported Database Types */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base">Supported Database Types</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid md:grid-cols-2 gap-4">
                            {/* SQL Databases */}
                            <div className="p-4 rounded-lg border bg-card">
                              <div className="flex items-center gap-2 mb-3">
                                <BarChart3 className="w-5 h-5 text-blue-500" />
                                <span className="font-medium">SQL Databases</span>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="p-2 rounded border bg-muted/30 flex items-center gap-2">
                                  <div className="w-6 h-6 rounded bg-blue-500/10 flex items-center justify-center">
                                    <Database className="w-3 h-3 text-blue-500" />
                                  </div>
                                  <span className="text-sm">PostgreSQL</span>
                                </div>
                                <div className="p-2 rounded border bg-muted/30 flex items-center gap-2">
                                  <div className="w-6 h-6 rounded bg-orange-500/10 flex items-center justify-center">
                                    <Database className="w-3 h-3 text-orange-500" />
                                  </div>
                                  <span className="text-sm">MySQL</span>
                                </div>
                                <div className="p-2 rounded border bg-muted/30 flex items-center gap-2">
                                  <div className="w-6 h-6 rounded bg-cyan-500/10 flex items-center justify-center">
                                    <Database className="w-3 h-3 text-cyan-500" />
                                  </div>
                                  <span className="text-sm">Azure SQL</span>
                                </div>
                                <div className="p-2 rounded border bg-muted/30 flex items-center gap-2">
                                  <div className="w-6 h-6 rounded bg-red-500/10 flex items-center justify-center">
                                    <Database className="w-3 h-3 text-red-500" />
                                  </div>
                                  <span className="text-sm">Google Cloud SQL</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Document Databases */}
                            <div className="p-4 rounded-lg border bg-card">
                              <div className="flex items-center gap-2 mb-3">
                                <FileText className="w-5 h-5 text-green-500" />
                                <span className="font-medium">Document Databases</span>
                                <Badge variant="secondary" className="text-xs">Coming Soon</Badge>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div className="p-2 rounded border bg-muted/30 flex items-center gap-2 opacity-60">
                                  <div className="w-6 h-6 rounded bg-green-500/10 flex items-center justify-center">
                                    <Layers className="w-3 h-3 text-green-500" />
                                  </div>
                                  <span className="text-sm">MongoDB Atlas</span>
                                </div>
                                <div className="p-2 rounded border bg-muted/30 flex items-center gap-2 opacity-60">
                                  <div className="w-6 h-6 rounded bg-orange-500/10 flex items-center justify-center">
                                    <Layers className="w-3 h-3 text-orange-500" />
                                  </div>
                                  <span className="text-sm">AWS DocumentDB</span>
                                </div>
                                <div className="p-2 rounded border bg-muted/30 flex items-center gap-2 opacity-60">
                                  <div className="w-6 h-6 rounded bg-blue-500/10 flex items-center justify-center">
                                    <Layers className="w-3 h-3 text-blue-500" />
                                  </div>
                                  <span className="text-sm">Azure Cosmos DB</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Collection Statistics */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Collection Statistics
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {dbStatus.collections && dbStatus.collections.length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                              {dbStatus.collections.map((collection) => (
                                <div key={collection.name} className="p-3 rounded-lg border bg-muted/30">
                                  <p className="text-2xl font-bold">{collection.document_count}</p>
                                  <p className="text-xs text-muted-foreground truncate" title={collection.name}>{collection.name}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-6 text-muted-foreground">
                              <Database className="w-8 h-8 mx-auto mb-2 opacity-50" />
                              <p>No collection data available</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Database Integration Status */}
                      <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
                          <div>
                            <p className="font-medium text-blue-600">Database Integration Status</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              External database integrations are currently in configuration-only mode. Save your credentials now, and the system will automatically use them when each provider is fully implemented.
                            </p>
                            <p className="text-sm text-muted-foreground mt-2">
                              <span className="font-medium">Implementation Roadmap:</span>{' '}
                              <span className="text-blue-600">PostgreSQL → MySQL → Azure SQL → Google Cloud SQL → MongoDB Atlas → DocumentDB → Cosmos DB</span>
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* External Database Providers List */}
                      {databaseProviders.length === 0 ? (
                        <Card>
                          <CardContent className="py-12">
                            <div className="text-center">
                              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                                <Database className="w-8 h-8 text-muted-foreground" />
                              </div>
                              <h4 className="font-medium mb-1">No External Database Configured</h4>
                              <p className="text-sm text-muted-foreground mb-4">Currently using local MongoDB. Add an external database provider to enable cloud-based data storage.</p>
                              <Button onClick={() => setShowAddDbProviderModal(true)}>
                                <Plus className="w-4 h-4 mr-2" />
                                Add Your First Database
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ) : (
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base">Configured Database Providers</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              {databaseProviders.map((provider) => (
                                <div
                                  key={provider.id}
                                  className={`p-4 rounded-lg border ${provider.is_active ? 'border-green-500 bg-green-500/5' : 'bg-card'}`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${provider.is_active ? 'bg-green-500/10' : 'bg-muted'}`}>
                                        <Database className={`w-5 h-5 ${provider.is_active ? 'text-green-500' : 'text-muted-foreground'}`} />
                                      </div>
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium">{provider.name}</span>
                                          <Badge variant="outline">{provider.provider_type}</Badge>
                                          {provider.is_active && (
                                            <Badge className="bg-green-500/10 text-green-600">Active</Badge>
                                          )}
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                          {provider.host}:{provider.port} / {provider.database_name}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDeleteDbProvider(provider.id)}
                                        className="text-destructive"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </TabsContent>

                  {/* Email Sub-tab */}
                  <TabsContent value="email" className="mt-0">
                    <div className="space-y-6">
                      <div className="p-4 rounded-lg bg-muted/50 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Current Configuration:</span>
                          {emailSettings.has_api_key ? (
                            <Badge className="bg-green-500/10 text-green-600">API Key Configured</Badge>
                          ) : (
                            <Badge className="bg-red-500/10 text-red-600">API Key Missing</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Sender: {emailSettings.sender_email || 'Not set'} ({emailSettings.sender_name || 'No name'})
                        </p>
                      </div>

                      <form onSubmit={handleSaveEmailSettings} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="sys-sender-email">Sender Email *</Label>
                          <Input
                            id="sys-sender-email"
                            type="email"
                            value={emailFormData.sender_email}
                            onChange={(e) => setEmailFormData({ ...emailFormData, sender_email: e.target.value })}
                            placeholder="noreply@yourdomain.com"
                          />
                          <p className="text-xs text-muted-foreground">
                            This email must be verified in your SendGrid account
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="sys-sender-name">Sender Name</Label>
                          <Input
                            id="sys-sender-name"
                            value={emailFormData.sender_name}
                            onChange={(e) => setEmailFormData({ ...emailFormData, sender_name: e.target.value })}
                            placeholder="DocuControl Service"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="sys-sendgrid-key">SendGrid API Key</Label>
                          <Input
                            id="sys-sendgrid-key"
                            type="password"
                            value={emailFormData.sendgrid_api_key}
                            onChange={(e) => setEmailFormData({ ...emailFormData, sendgrid_api_key: e.target.value })}
                            placeholder={emailSettings.has_api_key ? '••••••••••••••••' : 'Enter your SendGrid API key'}
                          />
                          <p className="text-xs text-muted-foreground">
                            Leave empty to keep existing key. Get your key from{' '}
                            <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                              SendGrid Dashboard
                            </a>
                          </p>
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleSendTestEmail}
                            disabled={sendingTestEmail || !emailSettings.has_api_key}
                          >
                            {sendingTestEmail ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Mail className="w-4 h-4 mr-2" />
                            )}
                            Send Test Email
                          </Button>
                          <Button type="submit" disabled={loading}>
                            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            Save Settings
                          </Button>
                        </div>
                      </form>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          {hasPermission('admin') && (
            <>
              {/* Document Types Tab */}
              <TabsContent value="document-types">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Document Types</CardTitle>
                      <CardDescription>
                        Manage document type classifications
                      </CardDescription>
                    </div>
                    <Button onClick={() => { setEditingDocType(null); setDocTypeFormData({ name: '', code: '', description: '' }); setShowDocTypeModal(true); }} data-testid="add-doc-type-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Type
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {documentTypes.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileType className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No document types defined</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {documentTypes.map((dt) => (
                          <div 
                            key={dt.id}
                            className="flex items-center justify-between p-4 rounded-lg border bg-card"
                            data-testid={`doc-type-${dt.id}`}
                          >
                            <div className="flex items-center gap-4">
                              <Badge variant="outline" className="font-mono">{dt.code}</Badge>
                              <div>
                                <p className="font-medium">{dt.name}</p>
                                {dt.description && (
                                  <p className="text-sm text-muted-foreground">{dt.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="icon" onClick={() => openEditDocType(dt)}>
                                <Edit className="w-4 h-4 text-muted-foreground" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteDocType(dt.id)}>
                                <Trash2 className="w-4 h-4 text-muted-foreground" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Revision Codes Tab */}
              <TabsContent value="revision-codes">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Revision Codes</CardTitle>
                      <CardDescription>
                        Manage document revision classifications
                      </CardDescription>
                    </div>
                    <Button onClick={() => { setEditingRevCode(null); setRevCodeFormData({ code: '', name: '', description: '', order: revisionCodes.length }); setShowRevCodeModal(true); }} data-testid="add-rev-code-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Code
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {revisionCodes.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No revision codes defined</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {revisionCodes.map((rc) => (
                          <div 
                            key={rc.id}
                            className="flex items-center justify-between p-4 rounded-lg border bg-card"
                            data-testid={`rev-code-${rc.id}`}
                          >
                            <div className="flex items-center gap-4">
                              <Badge className="font-mono">{rc.code}</Badge>
                              <div>
                                <p className="font-medium">{rc.name}</p>
                                {rc.description && (
                                  <p className="text-sm text-muted-foreground">{rc.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">Order: {rc.order}</span>
                              <Button variant="ghost" size="icon" onClick={() => openEditRevCode(rc)}>
                                <Edit className="w-4 h-4 text-muted-foreground" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteRevCode(rc.id)}>
                                <Trash2 className="w-4 h-4 text-muted-foreground" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Disciplines Tab */}
              <TabsContent value="disciplines">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Disciplines</CardTitle>
                      <CardDescription>
                        Manage engineering or project disciplines for documents
                      </CardDescription>
                    </div>
                    <Button onClick={() => { setEditingDiscipline(null); setDisciplineFormData({ code: '', name: '', description: '' }); setShowDisciplineModal(true); }} data-testid="add-discipline-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Discipline
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {disciplines.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No disciplines defined</p>
                        <p className="text-xs mt-1">Add disciplines like Mechanical, Electrical, Civil, etc.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {disciplines.map((d) => (
                          <div 
                            key={d.id}
                            className="flex items-center justify-between p-4 rounded-lg border bg-card"
                            data-testid={`discipline-${d.id}`}
                          >
                            <div className="flex items-center gap-4">
                              <Badge className="font-mono">{d.code}</Badge>
                              <div>
                                <p className="font-medium">{d.name}</p>
                                {d.description && (
                                  <p className="text-sm text-muted-foreground">{d.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="icon" onClick={() => openEditDiscipline(d)}>
                                <Edit className="w-4 h-4 text-muted-foreground" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteDiscipline(d.id)}>
                                <Trash2 className="w-4 h-4 text-muted-foreground" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Approval Codes Tab */}
              <TabsContent value="approval-codes">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Approval Codes</CardTitle>
                      <CardDescription>
                        Manage approval status codes for documents (e.g., AFC, IFR, IFA)
                      </CardDescription>
                    </div>
                    <Button onClick={() => { setEditingApprovalCode(null); setApprovalCodeFormData({ code: '', name: '', description: '' }); setShowApprovalCodeModal(true); }} data-testid="add-approval-code-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Approval Code
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {approvalCodes.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No approval codes defined</p>
                        <p className="text-xs mt-1">Add codes like AFC (Approved for Construction), IFR (Issued for Review)</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {approvalCodes.map((ac) => (
                          <div 
                            key={ac.id}
                            className="flex items-center justify-between p-4 rounded-lg border bg-card"
                            data-testid={`approval-code-${ac.id}`}
                          >
                            <div className="flex items-center gap-4">
                              <Badge variant="outline" className="font-mono bg-green-500/10 text-green-600 border-green-200">{ac.code}</Badge>
                              <div>
                                <p className="font-medium">{ac.name}</p>
                                {ac.description && (
                                  <p className="text-sm text-muted-foreground">{ac.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="icon" onClick={() => openEditApprovalCode(ac)}>
                                <Edit className="w-4 h-4 text-muted-foreground" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteApprovalCode(ac.id)}>
                                <Trash2 className="w-4 h-4 text-muted-foreground" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Status Codes Tab */}
              <TabsContent value="status-codes">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Status Codes</CardTitle>
                      <CardDescription>
                        Manage document workflow status codes
                      </CardDescription>
                    </div>
                    <Button onClick={() => { setEditingStatusCode(null); setStatusCodeFormData({ code: '', name: '', description: '', color: '' }); setShowStatusCodeModal(true); }} data-testid="add-status-code-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Status Code
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {statusCodes.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Tag className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No status codes defined</p>
                        <p className="text-xs mt-1">Add codes like WIP (Work in Progress), REV (Under Review)</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {statusCodes.map((sc) => (
                          <div 
                            key={sc.id}
                            className="flex items-center justify-between p-4 rounded-lg border bg-card"
                            data-testid={`status-code-${sc.id}`}
                          >
                            <div className="flex items-center gap-4">
                              <Badge 
                                className="font-mono" 
                                style={sc.color ? { backgroundColor: `${sc.color}20`, color: sc.color, borderColor: sc.color } : {}}
                              >
                                {sc.code}
                              </Badge>
                              <div>
                                <p className="font-medium">{sc.name}</p>
                                {sc.description && (
                                  <p className="text-sm text-muted-foreground">{sc.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {sc.color && (
                                <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: sc.color }} title={sc.color} />
                              )}
                              <Button variant="ghost" size="icon" onClick={() => openEditStatusCode(sc)}>
                                <Edit className="w-4 h-4 text-muted-foreground" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteStatusCode(sc.id)}>
                                <Trash2 className="w-4 h-4 text-muted-foreground" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Templates Tab */}
              <TabsContent value="templates">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Document Templates</CardTitle>
                      <CardDescription>
                        Create templates for quick document creation
                      </CardDescription>
                    </div>
                    <Button onClick={() => { setEditingTemplate(null); setTemplateFormData({ name: '', description: '', document_type_id: '', default_tags: '', content_template: '' }); setShowTemplateModal(true); }} data-testid="add-template-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Template
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {templates.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <LayoutTemplate className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No templates defined</p>
                        <p className="text-xs mt-1">Create templates to speed up document creation</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {templates.map((template) => (
                          <div 
                            key={template.id}
                            className="flex items-center justify-between p-4 rounded-lg border bg-card"
                            data-testid={`template-${template.id}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className="p-2 rounded-lg bg-muted">
                                <LayoutTemplate className="w-4 h-4 text-muted-foreground" />
                              </div>
                              <div>
                                <p className="font-medium">{template.name}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  {template.document_type && (
                                    <Badge variant="outline" className="text-xs">{template.document_type.code}</Badge>
                                  )}
                                  {template.description && (
                                    <p className="text-sm text-muted-foreground">{template.description}</p>
                                  )}
                                </div>
                                {template.default_tags?.length > 0 && (
                                  <div className="flex gap-1 mt-1">
                                    {template.default_tags.map((tag, i) => (
                                      <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="icon" onClick={() => openEditTemplate(template)}>
                                <Edit className="w-4 h-4 text-muted-foreground" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteTemplate(template.id)}>
                                <Trash2 className="w-4 h-4 text-muted-foreground" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Custom Fields Tab */}
              <TabsContent value="metadata">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Custom Metadata Fields</CardTitle>
                      <CardDescription>
                        Define custom fields for documents and assets
                      </CardDescription>
                    </div>
                    <Button onClick={() => setShowFieldModal(true)} data-testid="add-field-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Field
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {metadataFields.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Database className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No custom fields defined</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {metadataFields.map((field) => (
                          <div 
                            key={field.id}
                            className="flex items-center justify-between p-4 rounded-lg border bg-card"
                            data-testid={`metadata-field-${field.id}`}
                          >
                            <div>
                              <p className="font-medium">{field.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {field.field_type} • {field.entity_type}
                                {field.required && ' • Required'}
                              </p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteField(field.id)}>
                              <Trash2 className="w-4 h-4 text-muted-foreground" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              {/* Correspondence Folders Tab */}
              <TabsContent value="corr-folders">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle>Correspondence Folders</CardTitle>
                      <CardDescription>
                        Manage hierarchical folder structure for correspondence/mail organization
                      </CardDescription>
                    </div>
                    <Button onClick={() => { setEditingCorrFolder(null); setCorrFolderFormData({ name: '', description: '', parent_id: '' }); setShowCorrFolderModal(true); }} data-testid="add-corr-folder-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Folder
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {corrFolders.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No correspondence folders defined</p>
                        <p className="text-xs mt-1">Add folders like Inbox, Outbox, Archive, etc.</p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {(() => {
                          // Build hierarchical tree
                          const buildTree = (parentId = null, level = 0) => {
                            const children = corrFolders
                              .filter(f => (f.parent_id || null) === parentId)
                              .sort((a, b) => (a.order || 0) - (b.order || 0));
                            
                            return children.map((folder, index) => {
                              const siblings = children;
                              const hasChildren = corrFolders.some(f => f.parent_id === folder.id);
                              
                              return (
                                <div key={folder.id}>
                                  <div 
                                    className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                                    style={{ marginLeft: `${level * 24}px` }}
                                    data-testid={`corr-folder-${folder.id}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      {level > 0 && (
                                        <div className="w-4 h-4 border-l-2 border-b-2 border-muted-foreground/30 rounded-bl" />
                                      )}
                                      <FolderOpen className={`w-5 h-5 ${level === 0 ? 'text-primary' : 'text-muted-foreground'}`} />
                                      <div>
                                        <p className="font-medium">{folder.name}</p>
                                        {folder.description && (
                                          <p className="text-xs text-muted-foreground">{folder.description}</p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button 
                                        variant="ghost" 
                                        size="icon"
                                        onClick={() => handleMoveCorrFolder(folder.id, 'up', parentId)}
                                        disabled={index === 0}
                                        title="Move up"
                                      >
                                        <ChevronUp className="w-4 h-4 text-muted-foreground" />
                                      </Button>
                                      <Button 
                                        variant="ghost" 
                                        size="icon"
                                        onClick={() => handleMoveCorrFolder(folder.id, 'down', parentId)}
                                        disabled={index === siblings.length - 1}
                                        title="Move down"
                                      >
                                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                      </Button>
                                      <Button variant="ghost" size="icon" onClick={() => openEditCorrFolder(folder)}>
                                        <Edit className="w-4 h-4 text-muted-foreground" />
                                      </Button>
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        onClick={() => handleDeleteCorrFolder(folder.id)}
                                        disabled={hasChildren}
                                        title={hasChildren ? "Cannot delete folder with subfolders" : "Delete folder"}
                                      >
                                        <Trash2 className={`w-4 h-4 ${hasChildren ? 'text-muted-foreground/30' : 'text-muted-foreground'}`} />
                                      </Button>
                                    </div>
                                  </div>
                                  {buildTree(folder.id, level + 1)}
                                </div>
                              );
                            });
                          };
                          return buildTree();
                        })()}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Transmittal Statuses Tab */}
              <TabsContent value="transmittal-statuses">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Send className="w-5 h-5" />
                          Transmittal Statuses
                        </CardTitle>
                        <CardDescription>
                          Define statuses for tracking transmittals
                        </CardDescription>
                      </div>
                      <Button onClick={() => {
                        setEditingTransmittalStatus(null);
                        setTransmittalStatusFormData({ code: '', name: '', description: '', color: '', order: 0 });
                        setShowTransmittalStatusModal(true);
                      }} data-testid="add-transmittal-status-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Status
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {transmittalStatuses.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Send className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No transmittal statuses defined yet.</p>
                        <p className="text-sm">Add statuses like Draft, Sent, Received, Acknowledged.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {transmittalStatuses.map((status) => (
                          <div
                            key={status.id}
                            className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-4 h-4 rounded-full" 
                                style={{ backgroundColor: status.color || '#6b7280' }}
                              />
                              <Badge variant="outline" className="font-mono">
                                {status.code}
                              </Badge>
                              <span className="font-medium">{status.name}</span>
                              {status.description && (
                                <span className="text-sm text-muted-foreground">
                                  - {status.description}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditTransmittalStatus(status)}
                                data-testid={`edit-transmittal-status-${status.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteTransmittalStatus(status.id)}
                                className="text-destructive hover:text-destructive"
                                data-testid={`delete-transmittal-status-${status.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>

      {/* Create/Edit Document Type Modal */}
      <Dialog open={showDocTypeModal} onOpenChange={setShowDocTypeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingDocType ? 'Edit Document Type' : 'Add Document Type'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveDocType} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dt-code">Code</Label>
                <Input
                  id="dt-code"
                  value={docTypeFormData.code}
                  onChange={(e) => setDocTypeFormData({ ...docTypeFormData, code: e.target.value.toUpperCase() })}
                  placeholder="SPEC"
                  required
                  maxLength={10}
                  data-testid="doc-type-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dt-name">Name</Label>
                <Input
                  id="dt-name"
                  value={docTypeFormData.name}
                  onChange={(e) => setDocTypeFormData({ ...docTypeFormData, name: e.target.value })}
                  placeholder="Specification"
                  required
                  data-testid="doc-type-name-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dt-description">Description</Label>
              <Input
                id="dt-description"
                value={docTypeFormData.description}
                onChange={(e) => setDocTypeFormData({ ...docTypeFormData, description: e.target.value })}
                placeholder="Technical specifications"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDocTypeModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} data-testid="save-doc-type-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Revision Code Modal */}
      <Dialog open={showRevCodeModal} onOpenChange={setShowRevCodeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRevCode ? 'Edit Revision Code' : 'Add Revision Code'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveRevCode} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rc-code">Code</Label>
                <Input
                  id="rc-code"
                  value={revCodeFormData.code}
                  onChange={(e) => setRevCodeFormData({ ...revCodeFormData, code: e.target.value.toUpperCase() })}
                  placeholder="A"
                  required
                  maxLength={10}
                  data-testid="rev-code-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rc-order">Order</Label>
                <Input
                  id="rc-order"
                  type="number"
                  value={revCodeFormData.order}
                  onChange={(e) => setRevCodeFormData({ ...revCodeFormData, order: parseInt(e.target.value) || 0 })}
                  min={0}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rc-name">Name</Label>
              <Input
                id="rc-name"
                value={revCodeFormData.name}
                onChange={(e) => setRevCodeFormData({ ...revCodeFormData, name: e.target.value })}
                placeholder="Initial Release"
                required
                data-testid="rev-code-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rc-description">Description</Label>
              <Input
                id="rc-description"
                value={revCodeFormData.description}
                onChange={(e) => setRevCodeFormData({ ...revCodeFormData, description: e.target.value })}
                placeholder="First approved version"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowRevCodeModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} data-testid="save-rev-code-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Discipline Modal */}
      <Dialog open={showDisciplineModal} onOpenChange={setShowDisciplineModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingDiscipline ? 'Edit Discipline' : 'Add Discipline'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveDiscipline} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="discipline-code">Code</Label>
                <Input
                  id="discipline-code"
                  value={disciplineFormData.code}
                  onChange={(e) => setDisciplineFormData({ ...disciplineFormData, code: e.target.value })}
                  placeholder="e.g., MECH"
                  required
                  data-testid="discipline-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="discipline-name">Name</Label>
                <Input
                  id="discipline-name"
                  value={disciplineFormData.name}
                  onChange={(e) => setDisciplineFormData({ ...disciplineFormData, name: e.target.value })}
                  placeholder="e.g., Mechanical"
                  required
                  data-testid="discipline-name-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="discipline-desc">Description (optional)</Label>
              <Textarea
                id="discipline-desc"
                value={disciplineFormData.description}
                onChange={(e) => setDisciplineFormData({ ...disciplineFormData, description: e.target.value })}
                placeholder="Description of this discipline"
                data-testid="discipline-desc-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDisciplineModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} data-testid="save-discipline-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Approval Code Modal */}
      <Dialog open={showApprovalCodeModal} onOpenChange={setShowApprovalCodeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingApprovalCode ? 'Edit Approval Code' : 'Add Approval Code'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveApprovalCode} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="approval-code">Code</Label>
                <Input
                  id="approval-code"
                  value={approvalCodeFormData.code}
                  onChange={(e) => setApprovalCodeFormData({ ...approvalCodeFormData, code: e.target.value })}
                  placeholder="e.g., AFC"
                  required
                  data-testid="approval-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="approval-name">Name</Label>
                <Input
                  id="approval-name"
                  value={approvalCodeFormData.name}
                  onChange={(e) => setApprovalCodeFormData({ ...approvalCodeFormData, name: e.target.value })}
                  placeholder="e.g., Approved for Construction"
                  required
                  data-testid="approval-name-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="approval-desc">Description (optional)</Label>
              <Textarea
                id="approval-desc"
                value={approvalCodeFormData.description}
                onChange={(e) => setApprovalCodeFormData({ ...approvalCodeFormData, description: e.target.value })}
                placeholder="Description of this approval code"
                data-testid="approval-desc-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowApprovalCodeModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} data-testid="save-approval-code-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Status Code Modal */}
      <Dialog open={showStatusCodeModal} onOpenChange={setShowStatusCodeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingStatusCode ? 'Edit Status Code' : 'Add Status Code'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveStatusCode} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status-code">Code</Label>
                <Input
                  id="status-code"
                  value={statusCodeFormData.code}
                  onChange={(e) => setStatusCodeFormData({ ...statusCodeFormData, code: e.target.value })}
                  placeholder="e.g., WIP"
                  required
                  data-testid="status-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status-name">Name</Label>
                <Input
                  id="status-name"
                  value={statusCodeFormData.name}
                  onChange={(e) => setStatusCodeFormData({ ...statusCodeFormData, name: e.target.value })}
                  placeholder="e.g., Work in Progress"
                  required
                  data-testid="status-name-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status-desc">Description (optional)</Label>
              <Textarea
                id="status-desc"
                value={statusCodeFormData.description}
                onChange={(e) => setStatusCodeFormData({ ...statusCodeFormData, description: e.target.value })}
                placeholder="Description of this status code"
                data-testid="status-desc-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status-color">Color (optional)</Label>
              <div className="flex gap-2">
                <Input
                  id="status-color"
                  type="color"
                  value={statusCodeFormData.color || '#3b82f6'}
                  onChange={(e) => setStatusCodeFormData({ ...statusCodeFormData, color: e.target.value })}
                  className="w-12 h-10 p-1"
                  data-testid="status-color-input"
                />
                <Input
                  value={statusCodeFormData.color}
                  onChange={(e) => setStatusCodeFormData({ ...statusCodeFormData, color: e.target.value })}
                  placeholder="#3b82f6"
                  className="flex-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowStatusCodeModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} data-testid="save-status-code-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Correspondence Folder Modal */}
      <Dialog open={showCorrFolderModal} onOpenChange={setShowCorrFolderModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCorrFolder ? 'Edit Folder' : 'Add Folder'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveCorrFolder} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="folder-name">Folder Name</Label>
              <Input
                id="folder-name"
                value={corrFolderFormData.name}
                onChange={(e) => setCorrFolderFormData({ ...corrFolderFormData, name: e.target.value })}
                placeholder="e.g., Inbox, Outbox, Archive"
                required
                data-testid="corr-folder-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="folder-parent">Parent Folder (optional)</Label>
              <Select
                value={corrFolderFormData.parent_id || "none"}
                onValueChange={(value) => setCorrFolderFormData({ ...corrFolderFormData, parent_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="corr-folder-parent-select">
                  <SelectValue placeholder="Select parent folder (root if empty)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Root Level (No Parent) --</SelectItem>
                  {corrFolders
                    .filter(f => !editingCorrFolder || f.id !== editingCorrFolder.id) // Can't be own parent
                    .map((folder) => {
                      // Build path for display
                      const getPath = (f) => {
                        const parts = [f.name];
                        let current = f;
                        while (current.parent_id) {
                          const parent = corrFolders.find(p => p.id === current.parent_id);
                          if (parent) {
                            parts.unshift(parent.name);
                            current = parent;
                          } else break;
                        }
                        return parts.join(' / ');
                      };
                      return (
                        <SelectItem key={folder.id} value={folder.id}>
                          {getPath(folder)}
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="folder-desc">Description (optional)</Label>
              <Textarea
                id="folder-desc"
                value={corrFolderFormData.description}
                onChange={(e) => setCorrFolderFormData({ ...corrFolderFormData, description: e.target.value })}
                placeholder="Description of this folder"
                data-testid="corr-folder-desc-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCorrFolderModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} data-testid="save-corr-folder-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contact Modal */}
      {/* Transmittal Status Modal */}
      <Dialog open={showTransmittalStatusModal} onOpenChange={setShowTransmittalStatusModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTransmittalStatus ? 'Edit Transmittal Status' : 'Add Transmittal Status'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTransmittalStatus} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ts-code">Code *</Label>
                <Input
                  id="ts-code"
                  value={transmittalStatusFormData.code}
                  onChange={(e) => setTransmittalStatusFormData({ ...transmittalStatusFormData, code: e.target.value.toUpperCase() })}
                  placeholder="DFT"
                  required
                  maxLength={10}
                  data-testid="transmittal-status-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ts-name">Name *</Label>
                <Input
                  id="ts-name"
                  value={transmittalStatusFormData.name}
                  onChange={(e) => setTransmittalStatusFormData({ ...transmittalStatusFormData, name: e.target.value })}
                  placeholder="Draft"
                  required
                  data-testid="transmittal-status-name-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ts-desc">Description (optional)</Label>
              <Textarea
                id="ts-desc"
                value={transmittalStatusFormData.description}
                onChange={(e) => setTransmittalStatusFormData({ ...transmittalStatusFormData, description: e.target.value })}
                placeholder="Description of this status"
                data-testid="transmittal-status-desc-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ts-color">Color (optional)</Label>
              <div className="flex gap-2">
                <Input
                  id="ts-color"
                  type="color"
                  value={transmittalStatusFormData.color || '#3b82f6'}
                  onChange={(e) => setTransmittalStatusFormData({ ...transmittalStatusFormData, color: e.target.value })}
                  className="w-12 h-10 p-1"
                  data-testid="transmittal-status-color-input"
                />
                <Input
                  value={transmittalStatusFormData.color}
                  onChange={(e) => setTransmittalStatusFormData({ ...transmittalStatusFormData, color: e.target.value })}
                  placeholder="#3b82f6"
                  className="flex-1"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ts-order">Order</Label>
              <Input
                id="ts-order"
                type="number"
                value={transmittalStatusFormData.order}
                onChange={(e) => setTransmittalStatusFormData({ ...transmittalStatusFormData, order: parseInt(e.target.value) || 0 })}
                placeholder="0"
                data-testid="transmittal-status-order-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowTransmittalStatusModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} data-testid="save-transmittal-status-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Metadata Field Modal */}
      <Dialog open={showFieldModal} onOpenChange={setShowFieldModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom Field</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateField} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="field-name">Field Name</Label>
              <Input
                id="field-name"
                value={fieldFormData.name}
                onChange={(e) => setFieldFormData({ ...fieldFormData, name: e.target.value })}
                required
                data-testid="field-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="field-type">Field Type</Label>
                <Select
                  value={fieldFormData.field_type}
                  onValueChange={(value) => setFieldFormData({ ...fieldFormData, field_type: value })}
                >
                  <SelectTrigger data-testid="field-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="entity-type">Entity Type</Label>
                <Select
                  value={fieldFormData.entity_type}
                  onValueChange={(value) => setFieldFormData({ ...fieldFormData, entity_type: value })}
                >
                  <SelectTrigger data-testid="entity-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="document">Document</SelectItem>
                    <SelectItem value="asset">Asset</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(fieldFormData.field_type === 'select' || fieldFormData.field_type === 'multiselect') && (
              <div className="space-y-2">
                <Label htmlFor="options">Options (comma-separated)</Label>
                <Input
                  id="options"
                  value={fieldFormData.options}
                  onChange={(e) => setFieldFormData({ ...fieldFormData, options: e.target.value })}
                  placeholder="Option 1, Option 2, Option 3"
                />
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch
                id="required"
                checked={fieldFormData.required}
                onCheckedChange={(checked) => setFieldFormData({ ...fieldFormData, required: checked })}
              />
              <Label htmlFor="required">Required field</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowFieldModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} data-testid="create-field-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Field'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Template Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'Add Template'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTemplate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                value={templateFormData.name}
                onChange={(e) => setTemplateFormData({ ...templateFormData, name: e.target.value })}
                placeholder="Technical Specification Template"
                required
                data-testid="template-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="template-type">Document Type</Label>
              <Select
                value={templateFormData.document_type_id || "none"}
                onValueChange={(value) => setTemplateFormData({ ...templateFormData, document_type_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="template-type-select">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select document type</SelectItem>
                  {documentTypes.map((dt) => (
                    <SelectItem key={dt.id} value={dt.id}>
                      {dt.code} - {dt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="template-desc">Description</Label>
              <Textarea
                id="template-desc"
                value={templateFormData.description}
                onChange={(e) => setTemplateFormData({ ...templateFormData, description: e.target.value })}
                placeholder="Template for technical specification documents"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="template-tags">Default Tags (comma-separated)</Label>
              <Input
                id="template-tags"
                value={templateFormData.default_tags}
                onChange={(e) => setTemplateFormData({ ...templateFormData, default_tags: e.target.value })}
                placeholder="technical, spec, draft"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="template-content">Content Template (instructions/boilerplate)</Label>
              <Textarea
                id="template-content"
                value={templateFormData.content_template}
                onChange={(e) => setTemplateFormData({ ...templateFormData, content_template: e.target.value })}
                placeholder="Enter any instructions or boilerplate content for this template..."
                rows={4}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowTemplateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading || !templateFormData.document_type_id} data-testid="save-template-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Storage Provider Modal */}
      <Dialog open={showStorageModal} onOpenChange={(open) => { if (!open) resetStorageForm(); setShowStorageModal(open); }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cloud className="w-5 h-5" />
              {editingProvider ? 'Edit Storage Provider' : 'Add Storage Provider'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveStorageProvider} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="provider-name">Display Name</Label>
              <Input
                id="provider-name"
                value={storageFormData.name}
                onChange={(e) => setStorageFormData({ ...storageFormData, name: e.target.value })}
                placeholder="Company Cloud Storage"
                required
                data-testid="provider-name-input"
              />
              <p className="text-xs text-muted-foreground">A friendly name to identify this storage provider</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="provider-type">Provider Type</Label>
              <Select
                value={storageFormData.provider_type}
                onValueChange={handleProviderTypeChange}
                disabled={!!editingProvider}
              >
                <SelectTrigger data-testid="provider-type-select">
                  <SelectValue placeholder="Select a cloud provider" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(providerTypes).map(([key, type]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <span>{getProviderIcon(key)}</span>
                        {type.name}
                        <span className="text-xs text-muted-foreground ml-2">
                          ({type.auth_type === 'oauth2' ? 'OAuth' : 'API Key'})
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dynamic Config Fields */}
            {storageFormData.provider_type && providerTypes[storageFormData.provider_type] && (
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Settings2 className="w-4 h-4" />
                  Configuration
                </h4>
                
                {providerTypes[storageFormData.provider_type].auth_type === 'oauth2' && (
                  <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      <strong>OAuth Provider:</strong> After saving, you&apos;ll need to complete the OAuth authorization flow.
                    </p>
                  </div>
                )}

                {providerTypes[storageFormData.provider_type].config_fields.map((field) => (
                  <div key={field.key} className="space-y-2">
                    <Label htmlFor={`config-${field.key}`}>
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    <Input
                      id={`config-${field.key}`}
                      type={field.type === 'password' ? 'password' : 'text'}
                      value={storageFormData.config[field.key] || ''}
                      onChange={(e) => setStorageFormData({
                        ...storageFormData,
                        config: { ...storageFormData.config, [field.key]: e.target.value }
                      })}
                      placeholder={field.default || `Enter ${field.label.toLowerCase()}`}
                      required={field.required}
                      data-testid={`config-${field.key}-input`}
                    />
                    {field.help && (
                      <p className="text-xs text-muted-foreground">{field.help}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 pt-2">
              <Switch
                id="is-global-default"
                checked={storageFormData.is_global_default}
                onCheckedChange={(checked) => setStorageFormData({ ...storageFormData, is_global_default: checked })}
              />
              <Label htmlFor="is-global-default" className="cursor-pointer">
                Set as global default
              </Label>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              The global default provider is used for projects that don&apos;t have a specific storage provider assigned.
            </p>

            {/* Provider-specific help links */}
            {storageFormData.provider_type && (
              <div className="pt-4 border-t">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Setup Help
                </h4>
                <div className="text-sm text-muted-foreground space-y-1">
                  {storageFormData.provider_type === 's3' && (
                    <>
                      <p>• Create an IAM user with S3 access in <a href="https://console.aws.amazon.com/iam" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">AWS Console</a></p>
                      <p>• Required permissions: s3:PutObject, s3:GetObject, s3:DeleteObject, s3:ListBucket</p>
                    </>
                  )}
                  {storageFormData.provider_type === 'onedrive' && (
                    <>
                      <p>• Register an app in <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Azure Portal</a></p>
                      <p>• Add redirect URI: {window.location.origin}/api/oauth/callback/onedrive</p>
                      <p>• Use &quot;common&quot; as Tenant ID for personal accounts</p>
                    </>
                  )}
                  {storageFormData.provider_type === 'google_drive' && (
                    <>
                      <p>• Create credentials in <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Cloud Console</a></p>
                      <p>• Enable Google Drive API</p>
                      <p>• Add redirect URI: {window.location.origin}/api/oauth/callback/google</p>
                    </>
                  )}
                  {storageFormData.provider_type === 'dropbox' && (
                    <>
                      <p>• Create an app in <a href="https://www.dropbox.com/developers/apps" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Dropbox Developer Portal</a></p>
                      <p>• Choose &quot;Scoped access&quot; with &quot;Full Dropbox&quot; permissions</p>
                    </>
                  )}
                  {storageFormData.provider_type === 'sharepoint' && (
                    <>
                      <p>• Register an app in <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Azure Portal</a></p>
                      <p>• Grant Sites.ReadWrite.All and Files.ReadWrite.All permissions</p>
                      <p>• Get your SharePoint site URL from your admin</p>
                    </>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { resetStorageForm(); setShowStorageModal(false); }}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading || !storageFormData.provider_type || !storageFormData.name} 
                data-testid="save-provider-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingProvider ? 'Update Provider' : 'Add Provider')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Database Provider Modal */}
      <Dialog open={showDatabaseModal} onOpenChange={(open) => { if (!open) resetDatabaseForm(); setShowDatabaseModal(open); }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              {editingDbProvider ? 'Edit Database Provider' : 'Add Database Provider'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveDatabaseProvider} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="db-provider-name">Display Name</Label>
              <Input
                id="db-provider-name"
                value={databaseFormData.name}
                onChange={(e) => setDatabaseFormData({ ...databaseFormData, name: e.target.value })}
                placeholder="Production PostgreSQL"
                required
                data-testid="db-provider-name-input"
              />
              <p className="text-xs text-muted-foreground">A friendly name to identify this database</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="db-provider-type">Database Type</Label>
              <Select
                value={databaseFormData.provider_type}
                onValueChange={handleDbProviderTypeChange}
                disabled={!!editingDbProvider}
              >
                <SelectTrigger data-testid="db-provider-type-select">
                  <SelectValue placeholder="Select a database type" />
                </SelectTrigger>
                <SelectContent>
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">SQL Databases</div>
                  {Object.entries(databaseProviderTypes)
                    .filter(([, type]) => type.category === 'sql')
                    .map(([key, type]) => (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <span>{getDbProviderIcon(key)}</span>
                          {type.name}
                        </span>
                      </SelectItem>
                    ))}
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1 pt-2">Document Databases (Coming Soon)</div>
                  {Object.entries(databaseProviderTypes)
                    .filter(([, type]) => type.category === 'document')
                    .map(([key, type]) => (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2 opacity-60">
                          <span>{getDbProviderIcon(key)}</span>
                          {type.name}
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {databaseFormData.provider_type && databaseProviderTypes[databaseFormData.provider_type] && (
              <>
                {/* Config Mode Toggle */}
                <div className="space-y-2">
                  <Label>Configuration Mode</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={databaseFormData.config_mode === 'simple' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDatabaseFormData({ ...databaseFormData, config_mode: 'simple', config: {} })}
                    >
                      Simple (Connection String)
                    </Button>
                    <Button
                      type="button"
                      variant={databaseFormData.config_mode === 'advanced' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setDatabaseFormData({ ...databaseFormData, config_mode: 'advanced', config: {} })}
                    >
                      Advanced
                    </Button>
                  </div>
                </div>

                {/* Dynamic Config Fields */}
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="font-medium text-sm flex items-center gap-2">
                    <Settings2 className="w-4 h-4" />
                    Connection Settings
                  </h4>
                  
                  {databaseProviderTypes[databaseFormData.provider_type].coming_soon && (
                    <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800">
                      <p className="text-sm text-purple-800 dark:text-purple-200">
                        <strong>Coming Soon:</strong> {databaseProviderTypes[databaseFormData.provider_type].name} integration is on the roadmap. Save your configuration for when it&apos;s available.
                      </p>
                    </div>
                  )}

                  {(databaseFormData.config_mode === 'simple' 
                    ? databaseProviderTypes[databaseFormData.provider_type].simple_config 
                    : databaseProviderTypes[databaseFormData.provider_type].advanced_config
                  )?.map((field) => (
                    <div key={field.key} className="space-y-2">
                      <Label htmlFor={`db-config-${field.key}`}>
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      {field.type === 'select' ? (
                        <Select
                          value={databaseFormData.config[field.key] || field.default || ''}
                          onValueChange={(value) => setDatabaseFormData({
                            ...databaseFormData,
                            config: { ...databaseFormData.config, [field.key]: value }
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={`Select ${field.label}`} />
                          </SelectTrigger>
                          <SelectContent>
                            {field.options?.map((opt) => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : field.type === 'boolean' ? (
                        <div className="flex items-center gap-2">
                          <Switch
                            id={`db-config-${field.key}`}
                            checked={databaseFormData.config[field.key] ?? field.default ?? false}
                            onCheckedChange={(checked) => setDatabaseFormData({
                              ...databaseFormData,
                              config: { ...databaseFormData.config, [field.key]: checked }
                            })}
                          />
                          <Label htmlFor={`db-config-${field.key}`} className="font-normal cursor-pointer">
                            {field.label}
                          </Label>
                        </div>
                      ) : field.type === 'textarea' ? (
                        <textarea
                          id={`db-config-${field.key}`}
                          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          value={databaseFormData.config[field.key] || ''}
                          onChange={(e) => setDatabaseFormData({
                            ...databaseFormData,
                            config: { ...databaseFormData.config, [field.key]: e.target.value }
                          })}
                          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                          required={field.required}
                        />
                      ) : (
                        <Input
                          id={`db-config-${field.key}`}
                          type={field.type === 'password' ? 'password' : 'text'}
                          value={databaseFormData.config[field.key] || ''}
                          onChange={(e) => setDatabaseFormData({
                            ...databaseFormData,
                            config: { ...databaseFormData.config, [field.key]: e.target.value }
                          })}
                          placeholder={field.placeholder || field.default || `Enter ${field.label.toLowerCase()}`}
                          required={field.required}
                          data-testid={`db-config-${field.key}-input`}
                        />
                      )}
                      {field.help && (
                        <p className="text-xs text-muted-foreground">{field.help}</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Usage Mode */}
                <div className="space-y-2 pt-4 border-t">
                  <Label>Usage Mode</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div 
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${databaseFormData.usage_mode === 'primary' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                      onClick={() => setDatabaseFormData({ ...databaseFormData, usage_mode: 'primary' })}
                    >
                      <div className="flex items-center gap-2 font-medium">
                        <span className="w-3 h-3 rounded-full border-2 border-primary flex items-center justify-center">
                          {databaseFormData.usage_mode === 'primary' && <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>}
                        </span>
                        Primary Database
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Replace local MongoDB with this external database</p>
                    </div>
                    <div 
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${databaseFormData.usage_mode === 'sync' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                      onClick={() => setDatabaseFormData({ ...databaseFormData, usage_mode: 'sync' })}
                    >
                      <div className="flex items-center gap-2 font-medium">
                        <span className="w-3 h-3 rounded-full border-2 border-primary flex items-center justify-center">
                          {databaseFormData.usage_mode === 'sync' && <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>}
                        </span>
                        Sync / Backup
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Keep local MongoDB + mirror data to external</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <Switch
                    id="db-is-active"
                    checked={databaseFormData.is_active}
                    onCheckedChange={(checked) => setDatabaseFormData({ ...databaseFormData, is_active: checked })}
                  />
                  <Label htmlFor="db-is-active" className="cursor-pointer">
                    Enable this database connection
                  </Label>
                </div>
              </>
            )}

            {/* Provider-specific help links */}
            {databaseFormData.provider_type && (
              <div className="pt-4 border-t">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Setup Help
                </h4>
                <div className="text-sm text-muted-foreground space-y-1">
                  {databaseFormData.provider_type === 'postgresql' && (
                    <>
                      <p>• Create a PostgreSQL database on <a href="https://www.elephantsql.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">ElephantSQL</a>, <a href="https://supabase.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Supabase</a>, or <a href="https://neon.tech/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Neon</a></p>
                      <p>• Connection string format: postgresql://user:password@host:5432/database</p>
                    </>
                  )}
                  {databaseFormData.provider_type === 'mysql' && (
                    <>
                      <p>• Create a MySQL database on <a href="https://planetscale.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">PlanetScale</a> or <a href="https://www.freemysqlhosting.net/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">FreeMySQLHosting</a></p>
                      <p>• Connection string format: mysql://user:password@host:3306/database</p>
                    </>
                  )}
                  {databaseFormData.provider_type === 'azure_sql' && (
                    <>
                      <p>• Create in <a href="https://portal.azure.com/#create/Microsoft.SQLDatabase" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Azure Portal</a></p>
                      <p>• Get connection string from Azure Portal → SQL Database → Connection strings</p>
                    </>
                  )}
                  {databaseFormData.provider_type === 'google_cloud_sql' && (
                    <>
                      <p>• Create in <a href="https://console.cloud.google.com/sql" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Cloud Console</a></p>
                      <p>• Instance connection name format: project:region:instance</p>
                    </>
                  )}
                  {databaseFormData.provider_type === 'mongodb_atlas' && (
                    <p>• Create a free cluster at <a href="https://www.mongodb.com/atlas" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">MongoDB Atlas</a></p>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { resetDatabaseForm(); setShowDatabaseModal(false); }}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading || !databaseFormData.provider_type || !databaseFormData.name} 
                data-testid="save-db-provider-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingDbProvider ? 'Update Database' : 'Add Database')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
